// Utility to parse SCIM reference URLs and convert them to internal app routes

export interface ScimUrlInfo {
    entityType: string;
    entityId: string;
    originalUrl: string;
}

/**
 * Parses a SCIM reference URL to extract entity type and ID
 * Example: "https://dcps-core-api-dev.azurewebsites.net/api/scim/v2/entities/employee/00031801"
 * Returns: { entityType: "employee", entityId: "00031801" }
 */
export function parseScimReferenceUrl(url: string): ScimUrlInfo | null {
    try {
        const urlObj = new URL(url);

        // Extract path segments
        const pathSegments = urlObj.pathname.split('/').filter(segment => segment.length > 0);

        // Look for the pattern: /api/scim/v2/entities/{entityType}/{entityId}
        const entitiesIndex = pathSegments.findIndex(segment => segment === 'entities');

        if (entitiesIndex !== -1 && entitiesIndex + 2 < pathSegments.length) {
            const entityType = pathSegments[entitiesIndex + 1];
            const entityId = pathSegments[entitiesIndex + 2];

            if (entityType && entityId) {
                return {
                    entityType,
                    entityId,
                    originalUrl: url
                };
            }
        }

        return null;
    } catch (error) {
        console.warn('Failed to parse SCIM reference URL:', url, error);
        return null;
    }
}

/**
 * Converts a SCIM reference URL to an internal app route
 * Example: "https://dcps-core-api-dev.azurewebsites.net/api/scim/v2/entities/employee/00031801"
 * Returns: "#employee/00031801" for hash-based routing
 */
export function scimUrlToInternalRoute(url: string): string | null {
    const urlInfo = parseScimReferenceUrl(url);

    if (!urlInfo) {
        return null;
    }

    // For hash-based routing, return the format: #entityType/entityId
    return `#${urlInfo.entityType}/${urlInfo.entityId}`;
}

/**
 * Converts a SCIM reference URL to a full internal app route with section
 * Example: "https://dcps-core-api-dev.azurewebsites.net/api/scim/v2/entities/employee/00031801"
 * Returns: "#Person/employee/view/00031801" for hash-based routing (deep link with /view/)
 */
export function scimUrlToFullInternalRoute(url: string, resourceTypes?: any[]): string | null {
    const urlInfo = parseScimReferenceUrl(url);

    if (!urlInfo) {
        return null;
    }

    const lowerEntityType = urlInfo.entityType.toLowerCase();

    // Meta types: no /view/ in path
    if (lowerEntityType === 'schemaattribute') return `#meta/schemaattribute/${urlInfo.entityId}`;
    if (lowerEntityType === 'schemaentity') return `#meta/schemaentity/${urlInfo.entityId}`;
    if (lowerEntityType === 'schemabinding') return `#meta/schemabinding/${urlInfo.entityId}`;

    // Special routes: view without entity type in path
    if (lowerEntityType === 'group') return `#groups/view/${urlInfo.entityId}`;
    if (lowerEntityType === 'class') return `#classes/view/${urlInfo.entityId}`;

    // Use schema-driven categorization if resourceTypes are available
    if (resourceTypes && resourceTypes.length > 0) {
        // Find the resource type for this entity type
        const resourceType = resourceTypes.find(type =>
            type.name.toLowerCase() === lowerEntityType
        );

        if (resourceType) {
            // Extract schema attributes for categorization
            const schemaAttributes = resourceType.schema?.attributes || [];
            const attributeNames = schemaAttributes.map((attr: any) => attr.name?.toLowerCase() || '').filter(Boolean);

            // Key person indicators from schema attributes
            const keyPersonIndicators = [
                'userprincipalname', 'username', 'userName', 'emails', 'name',
                'displayName', 'active', 'externalId', 'id', 'meta', 'schemas'
            ];

            // Check for key person indicators
            const hasKeyPersonIndicators = keyPersonIndicators.some(indicator =>
                attributeNames.some((attr: string) => attr.includes(indicator.toLowerCase()))
            );

            // Check for person-related schema patterns
            const hasPersonSchema = resourceType.schema?.id?.includes('User') ||
                resourceType.schema?.id?.includes('Person') ||
                resourceType.schema?.name?.includes('User') ||
                resourceType.schema?.name?.includes('Person');

            const isPerson = hasKeyPersonIndicators || hasPersonSchema;

            if (isPerson) {
                return `#Person/${urlInfo.entityType}/view/${urlInfo.entityId}`;
            }
            // When schema says not person, fall through to sectionMapping so employee/applicant etc. still get #Person (hardcoded)
        }
    }

    // Fallback to hardcoded mappings for known types
    const sectionMapping: Record<string, string> = {
        'employee': 'Person',
        'person': 'Person',
        'applicant': 'Person',
        'contractor': 'Person',
        'public': 'Person',
        'shared': 'Person',
        'sponsored': 'Person',
        'student': 'Person',
        'vacancy': 'Person',
        'school': 'localities',
        'building': 'localities',
        'class': 'localities',
        'responsibilitycenter': 'localities',
        'studentbusstop': 'localities',
        'group': 'groups',
        'resourcetype': 'resourceTypes',
        'schema': 'schemas',
        'schemaentity': 'meta',
        'schemaattribute': 'meta'
    };

    const lower = urlInfo.entityType.toLowerCase();
    const section = sectionMapping[lower] || 'other';
    // Meta/resourceTypes/schemas: no /view/ in path
    if (section === 'meta' || section === 'resourceTypes' || section === 'schemas') {
        return `#${section}/${urlInfo.entityType}/${urlInfo.entityId}`;
    }
    return `#${section}/${urlInfo.entityType}/view/${urlInfo.entityId}`;
}

/**
 * Checks if a URL is a SCIM reference URL that we can handle internally
 */
export function isScimReferenceUrl(url: string): boolean {
    return parseScimReferenceUrl(url) !== null;
}

/**
 * Gets a display-friendly name for an entity type
 */
export function getEntityTypeDisplayName(entityType: string): string {
    const displayNames: Record<string, string> = {
        'employee': 'Employee',
        'person': 'Person',
        'applicant': 'Applicant',
        'contractor': 'Contractor',
        'public': 'Public User',
        'shared': 'Shared User',
        'sponsored': 'Sponsored User',
        'student': 'Student',
        'school': 'School',
        'building': 'Building',
        'class': 'Class',
        'responsibilitycenter': 'Responsibility Center',
        'group': 'Group',
        'resourcetype': 'Resource Type',
        'schema': 'Schema',
        'schemaentity': 'Schema Entity',
        'schemaattribute': 'Schema Attribute'
    };

    return displayNames[entityType.toLowerCase()] || entityType;
}
